(ns nosco.trial.trial-form
  (:require
   ["lottie-light-react$default" :as Lottie]
   ["react" :as React]
   ["react-dom/client" :as react-dom]
   [clojure.edn :as edn]
   [clojure.string :as string]
   [goog.object :as gobj]
   [hx.hooks :as hooks]
   [hx.react :refer [defnc] :as hx]
   [medley.core :as medley]
   [nosco.i18n.translate :as t]
   [nosco.icons]
   [nosco.icons2 :as icons2]
   [nosco.polyfills]
   [nosco.router :as router]
   [nosco.ui.base :as base]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.components :as components]
   [nosco.ui.forms :as forms]
   [nosco.ui.html :as html]
   [nosco.ui.inputs :as inputs]
   [nosco.ui.layouts :as layouts]
   [nosco.ui.loading :as loading]
   [nosco.ui.progress :as progress]
   [nosco.ui.text :as text]
   [nosco.ui.toast :as toast]
   [nosco.ui.welcome.welcome-components :as wc]
   [nosco.welcome.network :as wn]
   [reseda.react :as rr]
   [reseda.state]
   [shadow.resource :as shadow-resource]))

(defonce ^:export init-data
  (some->
   (gobj/get js/window "init_data_raw")
   (edn/read-string)))

(def hourglass-lottie
  (js/JSON.parse (shadow-resource/inline "animations/trial/hourglass-simple.json")))

(def success-lottie
  (js/JSON.parse (shadow-resource/inline "animations/trial/success-simple.json")))

(defnc FilterSVG []
  [html/UnsafeHTML {:html (shadow-resource/inline "animations/trial/animations.svg")}])

(defonce state (atom {:state :initial
                      #_#_:state :success
                      #_#_:success-message {"location" "https://foo.com"
                                            "email" "foo@foo.foo"
                                            "password" "BARBAR"}}))
(defonce store (reseda.state/new-store state))

(defn onRecaptchaLoad [_arg]
  (swap! state assoc :recaptcha-loaded true))

;; called by recaptcha when loaded, needed to avoid race conditions
(gobj/set js/window "noscoOnRecaptchaLoad" onRecaptchaLoad)

(defonce EL (js/document.getElementById "main"))
(defonce ROOT (react-dom/createRoot EL))

(defn render [component]
  (.render ^js ROOT component))

;; the initial validation takes 1-2 seconds, the only external dependencies there are
;; the recaptcha and mailgun validation. So rather than trying to model that precisely,
;; we assume that if 3 seconds have passed, the validation is over and we can show the
;; actual loading screen. We could model this via an SSE/websocket side channel that would send us
;; progress reports but for now we will just use a timeout.

(defn start-submission! []
  (swap! state assoc
         :in-flight true
         :validation-timeout (js/setTimeout
                              (fn []
                                (swap! state assoc
                                       :state :creating))
                              (:form-validation-delay init-data))))

(defn on-error [response]
  (when-let [timeout (get @state :validation-timeout)]
    (js/clearTimeout timeout))
  (swap! state assoc
         :in-flight false
         :state :initial
         :error (:response response)))

(defn on-success [response]
  (when-let [timeout (get @state :validation-timeout)]
    (js/clearTimeout timeout))
  (swap! state assoc
         :in-flight false
         :state :creating-done
         :success-message (get response "message")
         :error nil))

(defn trial-form-submit [form callback errback]
  (let [params (nosco.ui.forms/form-data form)]

    (wn/POST (.-action form)
      params
      callback
      errback)
    (start-submission!)))

(defn on-submit [e]
  (.preventDefault e)
  (swap! state assoc
         :in-flight false
         :error nil)
  (.execute js/grecaptcha))

(defn error-translation
  "Translate an error key into a friendly prompt."
  [k]
  (when k
    (if (and (sequential? k)
             (= "custom" (first k)))
      (second k)
      (get
       {"required" (t/t "This field is required.")
        "allowed-domains" (t/t "You need an approved domain to register.")
        "invalid-email" (t/t "Invalid email")
        "disposable-address" (t/t "Unacceptable email")
        "free-email" (t/t "Please use a company email address.")
        "validation-error" (t/t "Please fix the errors above.")}
       k (t/t "Something went wrong.")))))

(defn trial-errors->form-errors [error]
  (let [form-errors (get error "form-errors")]
    (medley/map-vals
     (fn [error-keys]
       (let [error-texts (keep error-translation error-keys)]
         (when error-texts
           (inputs/input-error (string/join "\n" error-texts)))))
     form-errors)))

(defnc Success []
  (let [success (rr/useStore store :success-message)
        {:strs [location email password]} success]
    [:div {:class "nosco-trial-success-wrapper"}
     [:div {:class "nosco-trial-success"}
      [:div {:style {:align-self "center"}}
       [Lottie {:animationData success-lottie
                ;;:className "wiggly-filter"
                :loop false
                :style {:width "120px"
                        :height "120px"}}]]
      [text/Text {:variant text/Heading-2xl}
       (t/t "All set, you're ready to go!")]
      [:div
       [text/Text {:variant text/Body-Md-Regular}
        (t/t "Visit the platform at:")]
       [:div {:class "mt-spacing-16"}
        [buttons2/Link
         {:class "w-100"
          :variant buttons2/NewSubtle
          :label location
          :href location
          :target "_blank"}]]]
      [:div
       [text/Text {:variant text/Heading-Md
                   :class "mb-spacing-16"
                   :el :div}
        "Login details:"]
       [text/Text {:variant text/Body-Md-Bold
                   :class "mb-spacing-4 text-extra-bold"
                   :el :div}
        "Username:"]
       [text/Text {:variant text/Body-Md-Regular} email]
       [text/Text {:variant text/Body-Md-Bold
                   :class "mb-spacing-4 mt-spacing-16 text-extra-bold"
                   :el :div}
        "Password:"]
       [layouts/Row {:gap "8px" :class "align-items-center"}
        [text/Text {:variant text/Body-Md-Regular} password] [inputs/CopyButton {:text password}]]]

      [layouts/Row  {:class "align-items-center"
                     :gap "8px"}
       [icons2/Icon {:icon icons2/Info-small}]
       [text/Text {:variant text/Body-Sm-Regular}
        "We’ve sent you an email with your login details for safe keeping."]]]]))

;; TODO validate early
(defnc TrialForm []
  (let [in-flight (rr/useStore store :in-flight)
        error (rr/useStore store :error)]
    [:div {:style (when in-flight
                    {:opacity 0.5
                     :pointer-events "none"})}
     [forms/Form
      {:form-desc (:form-desc init-data)
       :form-id "trial-form"
       :on-submit on-submit
       :form-values nil
       :form-errors (trial-errors->form-errors error)}
      [wc/Recaptcha {:site-key (:recaptcha-sitekey init-data)
                     :callback-fn (fn [_token]
                                            ;; recaptcha generates a hidden textarea so we get
                                            ;; the token from there -- no need to do anything with
                                            ;; the arg
                                    (js/console.log "received recaptcha token")

                                    (trial-form-submit (.getElementById js/document "trial-form")
                                                       on-success
                                                       on-error)
                                          ;; It seems that recaptcha has to be reset syncronously,
                                          ;; upon receiving the token, otherwise an error is thrown
                                          ;; while resetting the recaptcha object
                                    (.reset js/grecaptcha))}]
      (when-let [message (get error "message")]
        [wc/ErrorMessage {:text (error-translation message)}])
      [:div {:class "mt-spacing-32"}]
      [buttons2/Button
       {:type "submit"
        :variant buttons2/NewPrimary
        :loading in-flight
        :disabled in-flight
        :label (t/t "START TRIAL")
        :class "w-100"}]
      [wc/TermsContainer
       #_[wc/TermsConditionsText {:button-label "START TRIAL"
                                  :terms-conditions-display "show"
                                  :privacy-policy-display "show"}]
       [wc/RecaptchaText]]]]))

(defn hide-modal! []
  (router/push-state (router/current-route-name)
                     (router/current-route-path-params)
                     (dissoc (router/current-route-query-params) :modal)))

(defonce current-route-store (reseda.state/new-store router/current-route-match))
(defn subscribe-current-modal [route]
  (some-> route
          :query-params
          :modal
          keyword))

(defnc MarketingBullet [{:keys [text]}]
  [layouts/Row {:gap "16px" :class "align-items-center"}
   [icons2/Icon {:icon icons2/Check-large}]
   [text/Text {:variant text/Body-Lg-Regular}
    text]])

(defn generate-random-steps-adding-to
  "Generate n steps that are random and add up to x. Zeroes are allowed. "
  [n x]
  (let [steps (vec (repeat n 0))]
    (loop [steps steps
           left-x x]
      (if (zero? left-x)
        steps
        (let [pos (rand-int (count steps))
              step (min left-x (rand-int (int (/ x 14))))]
          (recur
           (update steps pos + step)
           (- left-x step)))))))


(defnc PreparingTrial [{:keys [finished]}]
  (let [[n set-n] (hooks/useState 0)
        [steps set-steps] (hooks/useState nil)
        progress (if finished 100 (reduce + (take n steps)))
        creation-estimated-time (:creation-estimated-time init-data)
        dt 200.0]
    (hooks/useEffect (fn []
                       (set-n 0)
                       (set-steps
                        (generate-random-steps-adding-to (int (/ creation-estimated-time dt)) 86))
                       (when finished
                         (js/setTimeout (fn []
                                          (swap! state assoc :state :success))
                                        2000))
                       (let [interval (js/setInterval
                                       (fn []
                                         (set-n inc))
                                       dt)]

                         #(js/clearInterval interval))) [finished])
    [:div {:class "nosco-trial-preparing"}
     [Lottie {:animationData hourglass-lottie
            ;;:className "wiggly-filter"
              :style {:width "120px"}}]
     [:div {:class "text-center"}
      [text/Text {:variant text/Heading-Md
                  :el :div
                  :class "mb-spacing-8"}
       "Preparing your site..."]
      [text/Text {:variant text/Body-Md-Regular
                  :el :div}
       "Hang tight, this might take a few seconds"]]
     [:div {:style {:width "300px"
                    :max-width "100%"}
            :class "px-spacing-24"}
      [progress/ProgressBar {:no-text true
                             :progress (/ progress 100.0)}]]]))

(defnc Form []
  [:div {:class "nosco-trial-main"}
   [:div {:class "nosco-trial-marketing"}
    [layouts/Column {:gap "24px" :class "mb-spacing-24"}
     [:div
      [components/StaticImage
       {:src "/images/diamond.png"
        :style {:width "120px"
                :height "120px"}}]]
     [text/Text {:variant text/Heading-2xl
                 :el :div :class "mb-spacing-16"}
      "Start your free 90-day Nosco trial"]

     [layouts/Column {:gap "12px"}
      [MarketingBullet {:text "Invite up to 10 team members"}]
      [MarketingBullet {:text "Create as many channels as you want"}]
      [MarketingBullet {:text "No credit card needed"}]]]]
   [:div {:class "nosco-trial-signup"}
    [:div {:class "welcome-main"}
     (if (not (rr/useStore store :recaptcha-loaded))
       [loading/LoadingNew]
       [TrialForm])]]])

(defnc Main []
  (hooks/useEffect
   (fn []
     (base/set-base-site-title! "Nosco Trial"))
   [])
  (let [current-modal (rr/useStore current-route-store subscribe-current-modal)
        page-state (rr/useStore store :state)]
    [React/StrictMode
     [nosco.icons/MainSVG]
     [FilterSVG]
     [:provider {:context components/StaticImageRootContext
                 :value (:static-server init-data)}
      [toast/ToastContainer
       [nosco.ui.base/ModalManager
        [wc/TextModal {:showing (= :terms-conditions current-modal)
                       :close hide-modal!
                       :title (or (:terms-conditions-link-title init-data)
                                  (t/t "Terms & Conditions"))
                       :html (:terms-conditions init-data)}]
        [wc/TextModal {:showing (= :privacy-policy current-modal)
                       :close hide-modal!
                       :title (or
                               (:privacy-policy-link-title init-data)
                               (t/t "Privacy Policy"))
                       :html (:privacy-policy-text init-data)}]
        [:button {:type "button"
                  :style {:opacity 0
                          :position "absolute"
                          :top 0
                          :left 0}
                  :on-click (fn []
                              (swap! state update :state {:initial :creating
                                                          :creating :creating-done
                                                          :creating-done :success
                                                          :success :initial}))}]

        (case page-state
          :success [Success]
          :creating [PreparingTrial]
          :creating-done [PreparingTrial {:finished true}]
          :initial [Form])]]]]))

(defn ^:dev/after-load -render []
  (render (hx/f [Main])))

(defn- handle-router-navigate [new-match]
  (reset! router/current-route-match
          new-match))
(def trial
  [["/" {:name ::trial}]])

(defn ^:export start []
  (router/start! trial handle-router-navigate)
  (render (hx/f [Main])))
